'use client';

import { cn } from '@/lib/utils';

export interface CitationLinkProps {
  regulationId?: string;
  caseLawCourtDecisionId?: string;
  articleNumber?: string;
  verseNumber?: string;
  children: React.ReactNode;
  onCitationClick?: (id: string, articleNumber?: string, verseNumber?: string, citationType?: 'regulation' | 'court_case') => void;
  className?: string;
}

export function CitationLink({
  regulationId,
  caseLawCourtDecisionId,
  articleNumber,
  verseNumber,
  children,
  onCitationClick,
  className,
}: CitationLinkProps) {
  const isCourtCase = !!caseLawCourtDecisionId;
  const id = isCourtCase ? caseLawCourtDecisionId : regulationId;
  const citationType = isCourtCase ? 'court_case' : 'regulation';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (id) {
      onCitationClick?.(id, articleNumber, verseNumber, citationType);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (id) {
        onCitationClick?.(id, articleNumber, verseNumber, citationType);
      }
    }
  };

  return (
    <span className="inline">
      <span>{children}</span>
      <sup
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'inline-flex items-center justify-center',
          'text-[11px] font-medium text-blue-600 hover:text-blue-800',
          'cursor-pointer transition-colors ml-0.5',
          className
        )}
        aria-label={`Lihat referensi: ${children}`}
      >
        [ref]
      </sup>
    </span>
  );
}

/**
 * Parses a regulation URL in the format:
 * - regulation:REGULATION_ID
 * - regulation:REGULATION_ID:ARTICLE_NUMBER
 * - regulation:REGULATION_ID:ARTICLE_NUMBER:VERSE_NUMBER
 *
 * @param href - The regulation URL to parse
 * @returns Parsed components or null if invalid format
 */
export function parseCitationUrl(href: string): {
  regulationId?: string;
  caseLawCourtDecisionId?: string;
  articleNumber?: string;
  verseNumber?: string;
  citationType: 'regulation' | 'court_case';
} | null {
  if (href.startsWith('regulation:')) {
    const parts = href.split(':');
    if (parts.length < 2) return null;
    const [, regulationId, articleNumber, verseNumber] = parts;
    if (!regulationId) return null;
    return { regulationId, articleNumber, verseNumber, citationType: 'regulation' };
  }

  if (href.startsWith('court_case:')) {
    const parts = href.split(':');
    if (parts.length < 2) return null;
    const [, caseLawCourtDecisionId] = parts;
    if (!caseLawCourtDecisionId) return null;
    return { caseLawCourtDecisionId, citationType: 'court_case' };
  }

  return null;
}
